// ABOUTME: UI preferences store. localStorage is the guest store and the instant-render cache;
// ABOUTME: for signed-in users usePrefsSync mirrors it to users/{uid}/settings/prefs.
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { ACCENT_PALETTES, DEFAULT_LIGHT_BG } from "../utils/adaptiveColor";
import { THEME_PRESETS } from "../constants/themePresets";

// A new user starts on the first designed preset (Chalk).
const DEFAULT_PRESET = THEME_PRESETS[0];

export type Spacing = "compact" | "default" | "relaxed";

// Pre-sync key the search widget wrote its engine choice to. Read once by the
// store migration below so nobody's choice resets.
const LEGACY_ENGINE_KEY = "start_search_engine_idx";

interface UIState {
  /** Single source of truth: the background color that drives the entire theme */
  baseColor: string;
  paletteIdx: number;
  columnCount: number;
  spacing: Spacing;
  /** Open dashboard links in a new tab. Default on — a start page is the page
   *  you navigate away from, and most users expect their links to launch out. */
  openLinksInNewTab: boolean;
  /** Index into SEARCH_ENGINES for the search widget and the header search launcher. */
  searchEngineIdx: number;
  /** Group dashboard links under headings derived from their tags. Default off
   *  so an existing dashboard keeps the arrangement its owner built. */
  groupingEnabled: boolean;
  /** Dashboard edit mode. Session state, not a preference — never persisted,
   *  so reloading never drops a user into edit mode. */
  editing: boolean;
  setBaseColor: (color: string) => void;
  setPaletteIdx: (idx: number) => void;
  setColumnCount: (count: number) => void;
  setSpacing: (spacing: Spacing) => void;
  setOpenLinksInNewTab: (openInNewTab: boolean) => void;
  setSearchEngineIdx: (idx: number) => void;
  setGroupingEnabled: (enabled: boolean) => void;
  setEditing: (editing: boolean) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      baseColor: DEFAULT_PRESET.background,
      paletteIdx: ACCENT_PALETTES.findIndex((name) => name === DEFAULT_PRESET.palette),
      columnCount: 3,
      spacing: "default" as Spacing,
      openLinksInNewTab: true,
      searchEngineIdx: 0,
      groupingEnabled: false,
      editing: false,

      setBaseColor: (baseColor) => set({ baseColor }),

      setPaletteIdx: (paletteIdx) => set({ paletteIdx }),

      setColumnCount: (columnCount) => set({ columnCount }),

      setSpacing: (spacing) => set({ spacing }),

      setOpenLinksInNewTab: (openLinksInNewTab) => set({ openLinksInNewTab }),

      setSearchEngineIdx: (searchEngineIdx) => set({ searchEngineIdx }),

      setGroupingEnabled: (groupingEnabled) => set({ groupingEnabled }),

      setEditing: (editing) => set({ editing }),
    }),
    {
      name: "ui-storage",
      version: 2,
      partialize: (state) => ({
        baseColor: state.baseColor,
        paletteIdx: state.paletteIdx,
        columnCount: state.columnCount,
        spacing: state.spacing,
        openLinksInNewTab: state.openLinksInNewTab,
        searchEngineIdx: state.searchEngineIdx,
        groupingEnabled: state.groupingEnabled,
      }),
      migrate: (persisted: unknown, version: number) => {
        let next = (persisted ?? {}) as Partial<UIState> & { theme?: string };
        // v0 -> v1: `theme` became `baseColor`.
        if (next && !next.baseColor) {
          const wasDark = next.theme === "dark";
          next = { ...next, baseColor: wasDark ? "#09090b" : DEFAULT_LIGHT_BG };
        }
        // v1 -> v2: the search engine choice moves in from its own key.
        if (version < 2 && next.searchEngineIdx === undefined) {
          try {
            const stored = Number(localStorage.getItem(LEGACY_ENGINE_KEY));
            if (Number.isInteger(stored) && stored >= 0) {
              next = { ...next, searchEngineIdx: stored };
            }
            localStorage.removeItem(LEGACY_ENGINE_KEY);
          } catch {
            // localStorage unavailable — fall through to the default.
          }
        }
        return next as UIState;
      },
    }
  )
);
